"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import AlertDialog from "./AlertDialog";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const safeNumber = (val, d = 0) => {
  const p = parseFloat(val);
  return isNaN(p) ? d : p;
};

const calculateCoverageFromSize = (size, uom) => {
  if (!size) return 1;
  const u = (uom || "").toLowerCase();
  const isSF = u.includes("sf") || u.includes("square");
  const isLF = u === "lf" || u.includes("linear") || u.includes("lin ft");
  const parseToFeet = (s) => {
    s = String(s).trim();

    // Helper: parse a value that may contain a fraction like "4-3/4" or "3/4" or "4.5"
    const parseWithFraction = (str) => {
      str = String(str).trim().replace(/"$/, "");
      // Mixed number: "4-3/4" or "4 3/4"
      const mixed = str.match(/^([\d.]+)[\s-]([\d.]+)\/([\d.]+)$/);
      if (mixed)
        return (
          parseFloat(mixed[1]) + parseFloat(mixed[2]) / parseFloat(mixed[3])
        );
      // Pure fraction: "3/4"
      const frac = str.match(/^([\d.]+)\/([\d.]+)$/);
      if (frac) return parseFloat(frac[1]) / parseFloat(frac[2]);
      // Plain number
      return parseFloat(str) || 0;
    };

    // Pattern: feet'inches" where inches may have fractions — e.g. 10' 4-3/4"
    let m = s.match(/^([\d.]+)'[\s]*([\d]+[\s-][\d]+\/[\d]+)"?$/);
    if (m) return parseFloat(m[1]) + parseWithFraction(m[2]) / 12;

    // Pattern: feet'inches" where inches is a simple fraction — e.g. 10' 3/4"
    m = s.match(/^([\d.]+)'[\s]*([\d]+\/[\d]+)"?$/);
    if (m) return parseFloat(m[1]) + parseWithFraction(m[2]) / 12;

    // Pattern: feet'inches" where inches is a decimal — e.g. 10'4" or 10' 4.5"
    m = s.match(/^([\d.]+)'[\s]*([\d.]+)"?$/);
    if (m) return parseFloat(m[1]) + (parseFloat(m[2]) || 0) / 12;

    // Pattern: feet' inches" with space — e.g. 10' 4"
    m = s.match(/^([\d.]+)'?\s+([\d.]+)"?$/);
    if (m) return parseFloat(m[1]) + (parseFloat(m[2]) || 0) / 12;

    // Just feet — e.g. 10'
    if (s.includes("'") && !s.includes('"'))
      return parseFloat(s.replace("'", "")) || 0;

    // Just inches — e.g. 4-3/4" or 4.75"
    if (s.endsWith('"') && !s.includes("'"))
      return parseWithFraction(s.slice(0, -1)) / 12;

    return parseFloat(s) || 0;
  };
  if (isSF) {
    const p = String(size)
      .replace(/×/g, "x")
      .split(/\s*[xX]\s*/);
    if (p.length === 2) {
      const w = parseToFeet(p[0]),
        h = parseToFeet(p[1]);
      if (w > 0 && h > 0) return Number((w * h).toFixed(2));
    }
  }
  if (isLF) {
    const l = parseToFeet(size);
    if (l > 0) return Number(l.toFixed(2));
  }
  return 1;
};

const getCoverage = (item) => {
  if (!item) return 1;
  if (
    item.coveragePerUnit &&
    parseFloat(item.coveragePerUnit) > 0 &&
    parseFloat(item.coveragePerUnit) !== 1
  )
    return parseFloat(item.coveragePerUnit);
  const uom = item.uom || item.lineItem?.uom || item.lineItem?.container || "";
  const size = item.size || item.lineItem?.size || "";
  return calculateCoverageFromSize(size, uom);
};

export default function StockOutOrderDetailView() {
  const { id: projectId, orderId } = useParams();
  const navigate = useNavigate();

  // Core data
  const [order, setOrder] = useState(null);
  const [project, setProject] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [linkedLaborItems, setLinkedLaborItems] = useState([]);

  // Editable items (built from order.items on load)
  const [editableItems, setEditableItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [varianceReasonCategory, setVarianceReasonCategory] = useState("");
  // Labor variance overrides
  const [laborRateOverrides, setLaborRateOverrides] = useState({});
  const [laborDeltaOverrides, setLaborDeltaOverrides] = useState({});

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [groupByItem, setGroupByItem] = useState(false);
  const [showVariancePanel, setShowVariancePanel] = useState(true);
  const [varianceReason, setVarianceReason] = useState("");

  // Table filters
  const [tableFilters, setTableFilters] = useState({
    conditionName: [],
    sectionNumber: [],
    size: [],
    uom: [],
    bidArea: [],
    descriptionSearch: "",
  });

  const [showTableFilter, setShowTableFilter] = useState(null);
  const tableConditionRef = useRef(null);
  const tableSectionRef = useRef(null);
  const tableSizeRef = useRef(null);
  const tableUomRef = useRef(null);
  const tableBidAreaRef = useRef(null);
  // Add this ref near the top with other refs

  const multiSelectFilterStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#1f2937",
      borderColor: state.isFocused ? "#f97316" : "#4b5563",
      color: "#e5e7eb",
      boxShadow: "none",
      minHeight: "50px",
      fontSize: "14px",
      "&:hover": { borderColor: "#f97316" },
    }),
    singleValue: (base) => ({
      ...base,
      color: "#e5e7eb",
      fontWeight: "500",
      padding: "2px 4px",
      fontSize: 12,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#4b5563",
      borderRadius: "6px",
      margin: 0,
      fontSize: "13px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#e5e7eb",
      padding: "3px 8px",
      paddingRight: "6px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#9ca3af",
      cursor: "pointer",
      paddingLeft: "4px",
      paddingRight: "4px",
      borderRadius: "4px",
      ":hover": { backgroundColor: "#f97316", color: "white" },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
      fontSize: "14px",
    }),
    input: (base) => ({ ...base, color: "#e5e7eb" }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#374151",
      borderRadius: "0.5rem",
      boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
      border: "1px solid #4b5563",
      zIndex: 999999,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "380px",
      padding: "8px",
      overflowY: "auto",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#4b5563" : "#374151",
      color: state.isSelected ? "#f97316" : "#e5e7eb",
      padding: "10px 12px",
      cursor: "pointer",
      fontSize: "13.5px",
    }),
  };

  // Email/PDF state
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [vendorPdfBuffer, setVendorPdfBuffer] = useState(null);
  const [sitePdfBuffer, setSitePdfBuffer] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [surcharges, setSurcharges] = useState([]);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
    signature: "",
  });
  const [formData, setFormData] = useState({
    shippingMethod: "Delivery",
    deliveryInstructions: "",
    expectedDeliveryDate: "",
    fieldContact: [],
    deliveryContact: null,
    contact: [],
  });

  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    [],
  );
  const userRole = localStorage.getItem("userRole");

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // ─── Fetch all data ──────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const headers = getHeaders();
        try {
          const d = jwtDecode(localStorage.getItem("token"));
          setUserEmail(d.email || "");
        } catch {}

        const [
          orderRes,
          projectRes,
          vendorsRes,
          contactsRes,
          usersRes,
          companyRes,
        ] = await Promise.all([
          axios.get(`${API_BASE}/stock-out-orders/${orderId}`, { headers }),
          axios.get(`${API_BASE}/projects/${projectId}`, { headers }),
          axios.get(`${API_BASE}/accounts/vendors`, { headers }),
          axios.get(`${API_BASE}/contacts`, { headers }),
          axios.get(`${API_BASE}/users`, { headers }),
          axios.get(`${API_BASE}/company-profile`, { headers }),
        ]);

        const od = orderRes.data;
        const pd = projectRes.data;

        setOrder(od);
        setFormData({
          shippingMethod: od.shippingMethod || "Delivery",
          deliveryInstructions: od.deliveryInstructions || "",
          expectedDeliveryDate: od.expectedDeliveryDate
            ? od.expectedDeliveryDate.split("T")[0]
            : "",
          fieldContact: Array.isArray(od.fieldContact)
            ? od.fieldContact.map((u) => u._id || u)
            : [],
          deliveryContact:
            od.deliveryContact?._id || od.deliveryContact || null,
          contact: Array.isArray(od.contact)
            ? od.contact.map((c) => c._id || c)
            : [],
        });

        setProject(pd);
        setVendors(vendorsRes.data);
        setContacts(contactsRes.data);
        setUsers(usersRes.data);
        setCompanyProfile(companyRes.data);

        setLinkedLaborItems(od.linkedLaborItems || []);

        setVarianceReasonCategory(
          od.stockOutReasonCategory || od.varianceReasonCategory || "",
        );

        setVarianceReason(od.stockOutReason || "");
        setSurcharges(od.surcharges || []);

        const items = (od.items || []).map((item) => {
          const li = item.lineItem || {};
          const uom = item.uom || li.uom || li.container || "EA";
          const origSize = item.size || li.size || "";
          const origPrice = item.stockOutOriginalVendorPrice || li.price || 0;
          const newPrice = item.price || 0;
          const cov = getCoverage(item);
          return {
            _id: item._id,
            assembly: item.assembly,
            lineItemId: item.lineItemId,
            quantity: item.quantity || 0,
            size: origSize,
            originalSize: origSize,
            uom,
            price: newPrice,
            originalPrice: origPrice,
            stockOutOriginalVendorPrice: origPrice,
            stockOutNewVendorPrice: newPrice,
            coveragePerUnit: cov,
            originalCoverage: cov,
            bidArea: item.bidArea || li.bidArea || "",
            conditionName: item.conditionName || "",
            costCodeDescription:
              item.costCodeDescription ||
              li.costCodeDescription ||
              li.itemDescription ||
              "",
            materialCode: li.materialCode || "",
            sectionNumber: li.sectionNumber || "",
            laborId: item.laborId || li.laborId || "",
            originalVendorName:
              item.originalVendorName || li.vendor?.companyName || "",
            lineItem: li,
            // Inventory flags
            isInventorySource: !!(
              item.isInventorySource || item.inventoryItemId
            ),
            inventoryItemId: item.inventoryItemId || null,
          };
        });
        setEditableItems(items);

        const ce = contactsRes.data
          .filter((c) =>
            (Array.isArray(od.contact)
              ? od.contact.map((x) => x._id || x)
              : []
            ).includes(c._id),
          )
          .map((c) => c.email)
          .filter(Boolean)
          .join(", ");
        const pc = contactsRes.data.find((c) =>
          (Array.isArray(od.contact)
            ? od.contact.map((x) => x._id || x)
            : []
          ).includes(c._id),
        );
        setEmailForm({
          to: ce || "",
          subject: `${pd.projectName} - PO#${pd.projectNumber}-${od.materialOrderNumber}`,

          body: `${pc?.firstName ? `Hi ${pc.firstName},` : "Dear Vendor,"}\n\nPlease find attached material order ${od.materialOrderNumber} for your review and action.\n\nBest regards,`,
          signature: "",
        });
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [orderId, projectId, getHeaders]);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddSurcharge = useCallback(() => {
    setSurcharges((prev) => [...prev, { description: "", price: 0 }]);
  }, []);

  const handleSurchargeChange = useCallback((index, field, value) => {
    setSurcharges((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  }, []);

  const handleRemoveSurcharge = useCallback((index) => {
    setSurcharges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const contactOptions = useMemo(() => {
    const vendorId = order?.vendor?._id || order?.vendor;
    return contacts
      .filter((c) => c.relatedAccount === vendorId)
      .map((c) => ({
        value: c._id,
        label: `${c.firstName} ${c.lastName} (${c.email})`,
      }));
  }, [contacts, order]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u._id,
        label: `${u.displayName || "User"} (${u.phoneNumber || "N/A"})`,
      })),
    [users],
  );

  useEffect(() => {
    if (!contacts.length || !formData.contact.length) return;
    const selectedEmails = contacts
      .filter((c) => formData.contact.includes(c._id))
      .map((c) => c.email)
      .filter(Boolean)
      .join(", ");
    setEmailForm((prev) => ({ ...prev, to: selectedEmails }));
  }, [formData.contact, contacts]);

  // ─── Click outside to close filter dropdowns ─────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showTableFilter) return;
      const refs = {
        conditionName: tableConditionRef,
        sectionNumber: tableSectionRef,
        size: tableSizeRef,
        uom: tableUomRef,
        bidArea: tableBidAreaRef,
      };
      const activeRef = refs[showTableFilter];
      if (activeRef?.current?.contains(event.target)) return;
      const isReactSelect =
        event.target.closest('[class*="css-"]') ||
        event.target.closest(".react-select__menu") ||
        event.target.closest(".react-select__menu-portal") ||
        event.target.id?.startsWith("react-select");
      if (isReactSelect) return;
      setShowTableFilter(null);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showTableFilter]);

  const normalizeConditionName = (conditionName) => {
    if (!conditionName) return conditionName;
    const str = String(conditionName).trim();
    const match = str.match(/^\d+\s*[-–—]\s*(.+)$/);
    return match ? match[1].trim() : str;
  };

  // ─── Filter options ──────────────────────────────────────────────
  const tableFilterOptions = useMemo(() => {
    const allConditions = [
      ...new Set(
        editableItems
          .map((i) => normalizeConditionName(i.conditionName))
          .filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const allSections = [
      ...new Set(editableItems.map((i) => i.sectionNumber).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const allSizes = [
      ...new Set(editableItems.map((i) => i.size).filter(Boolean)),
    ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const allUoms = [
      ...new Set(editableItems.map((i) => i.uom).filter(Boolean)),
    ].sort();

    // Sizes relevant to current condition/section/uom filters
    const activeFilteredItems = editableItems.filter((item) => {
      const normCond = normalizeConditionName(item.conditionName);
      if (
        tableFilters.conditionName.length > 0 &&
        !tableFilters.conditionName.includes(normCond)
      )
        return false;
      if (
        tableFilters.sectionNumber.length > 0 &&
        !tableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (tableFilters.uom.length > 0 && !tableFilters.uom.includes(item.uom))
        return false;
      return true;
    });

    const sizesInCurrentFilter = new Set(
      activeFilteredItems.map((i) => i.size).filter(Boolean),
    );

    const hasOtherActiveFilters =
      tableFilters.conditionName.length > 0 ||
      tableFilters.sectionNumber.length > 0 ||
      tableFilters.uom.length > 0;

    const sizeOptions = hasOtherActiveFilters
      ? [
          ...allSizes
            .filter((s) => sizesInCurrentFilter.has(s))
            .map((s) => ({ value: s, label: s, inFilter: true })),
          ...allSizes
            .filter((s) => !sizesInCurrentFilter.has(s))
            .map((s) => ({ value: s, label: s, inFilter: false })),
        ]
      : allSizes.map((s) => ({ value: s, label: s, inFilter: false }));

    const allBidAreas = [
      ...new Set(editableItems.map((i) => i.bidArea).filter(Boolean)),
    ].sort();

    return {
      conditionName: allConditions,
      sectionNumber: allSections,
      size: allSizes,
      sizeOptions,
      uom: allUoms,
      bidArea: allBidAreas,
    };
  }, [editableItems, tableFilters]);

  // ─── Filtered items ──────────────────────────────────────────────
  const filteredEditableItems = useMemo(() => {
    let items = editableItems.filter((item) => {
      const normCond = normalizeConditionName(item.conditionName);
      if (
        tableFilters.conditionName.length > 0 &&
        !tableFilters.conditionName.includes(normCond)
      )
        return false;
      if (
        tableFilters.sectionNumber.length > 0 &&
        !tableFilters.sectionNumber.includes(item.sectionNumber)
      )
        return false;
      if (
        tableFilters.size.length > 0 &&
        !tableFilters.size.includes(item.size)
      )
        return false;
      if (tableFilters.uom.length > 0 && !tableFilters.uom.includes(item.uom))
        return false;

      if (
        tableFilters.bidArea.length > 0 &&
        !tableFilters.bidArea.includes(item.bidArea)
      )
        return false;

      return true;
    });

    if (tableFilters.descriptionSearch) {
      items = soMultiTokenSearch(items, tableFilters.descriptionSearch);
    }

    return items;
  }, [editableItems, tableFilters]);

  const filteredGroupedItems = useMemo(() => {
    const map = new Map();
    filteredEditableItems.forEach((item) => {
      const key = `${item.materialCode}|||${item.costCodeDescription}|||${item.size}|||${item.price}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          description: item.costCodeDescription,
          materialCode: item.materialCode,
          size: item.size,
          uom: item.uom,
          coverage: getCoverage(item),
          origPrice: item.stockOutOriginalVendorPrice || 0,
          newPrice: item.price || 0,
          totalQty: 0,
          conditions: new Set(),
          bidAreas: new Set(),
          originalVendorName: item.originalVendorName,
          isInventorySource: item.isInventorySource,
          _items: [],
        });
      }
      const g = map.get(key);
      g.totalQty += item.quantity || 0;
      if (item.conditionName) g.conditions.add(item.conditionName);
      if (item.bidArea) g.bidAreas.add(item.bidArea);
      g._items.push(item);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.materialCode || a.description || "").localeCompare(
        b.materialCode || b.description || "",
      ),
    );
  }, [filteredEditableItems]);

  const filteredTotals = useMemo(
    () => ({
      itemsTotal: filteredEditableItems.reduce(
        (s, i) => s + (i.quantity || 0) * getCoverage(i) * (i.price || 0),
        0,
      ),
      totalQuantity: filteredEditableItems.reduce(
        (s, i) => s + (i.quantity || 0),
        0,
      ),
    }),
    [filteredEditableItems],
  );

  const hasActiveFilters = useMemo(
    () =>
      Object.values(tableFilters).some((f) =>
        Array.isArray(f) ? f.length > 0 : !!f,
      ),
    [tableFilters],
  );

  const handleTableFilterChange = useCallback((type, value) => {
    if (type === "descriptionSearch")
      setTableFilters((p) => ({ ...p, descriptionSearch: value }));
    else setTableFilters((p) => ({ ...p, [type]: value || [] }));
  }, []);

  const clearAllTableFilters = useCallback(() => {
    setTableFilters({
      conditionName: [],
      sectionNumber: [],
      size: [],
      uom: [],
      bidArea: [],
      descriptionSearch: "",
    });
  }, []);

  const toggleTableFilter = useCallback((field) => {
    setShowTableFilter((p) => (p === field ? null : field));
  }, []);

  // ─── Item editing handlers ───────────────────────────────────────
  const handleItemChange = useCallback((lineItemId, field, value) => {
    setEditableItems((prev) =>
      prev.map((item) => {
        if (item.lineItemId !== lineItemId) return item;
        return { ...item, [field]: value };
      }),
    );
  }, []);

  const handleSizeChange = useCallback((lineItemId, newSize) => {
    setEditableItems((prev) =>
      prev.map((item) => {
        if (item.lineItemId !== lineItemId) return item;
        const newCov = calculateCoverageFromSize(newSize, item.uom);
        return {
          ...item,
          size: newSize,
          coveragePerUnit: newCov > 0 ? newCov : 1,
        };
      }),
    );
  }, []);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(
        `${API_BASE}/stock-out-orders/${orderId}`,
        {
          shippingMethod: formData.shippingMethod,
          deliveryInstructions: formData.deliveryInstructions,
          expectedDeliveryDate: formData.expectedDeliveryDate || null,
          fieldContact: formData.fieldContact,
          deliveryContact: formData.deliveryContact,
          contact: formData.contact,
          surcharges,
          varianceReason,
          varianceReasonCategory,
          items: editableItems.map((i) => ({
            assembly: i.assembly,
            lineItemId: i.lineItemId,
            quantity: i.quantity,
            size: i.size,
            price: i.price,
            coveragePerUnit: i.coveragePerUnit,
            bidArea: i.bidArea,
            stockOutNewVendorPrice: i.price,
            stockOutOriginalVendorPrice: i.stockOutOriginalVendorPrice,
          })),
        },
        { headers: getHeaders() },
      );
      setDialog({
        open: true,
        type: "success",
        title: "Saved",
        message: "Changes saved successfully.",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Variance summary (only relevant for vendor orders) ──────────
  const varianceSummary = useMemo(() => {
    // Inventory-sourced orders never produce variance
    if (order?.isInventorySource) {
      return {
        changes: [],
        laborChanges: [],
        totalMaterialDelta: 0,
        totalLaborDelta: 0,
        totalDelta: 0,
        hasChanges: false,
      };
    }

    const changes = [];
    let totalMaterialDelta = 0;
    const laborCoverageChanges = [];

    editableItems.forEach((item) => {
      if (item.isInventorySource || item.inventoryItemId) return;

      const currentCov = getCoverage(item);
      const qty = safeNumber(item.quantity, 0);
      const newPrice = safeNumber(item.price, 0);
      const origPrice = safeNumber(item.stockOutOriginalVendorPrice, 0);
      const origSize = item.originalSize || "";
      const origCov =
        item.originalCoverage || calculateCoverageFromSize(origSize, item.uom);

      const sizeChanged =
        item.size?.trim() !== origSize.trim() &&
        origSize.trim() !== "" &&
        item.size?.trim() !== "";
      const priceChanged = Math.abs(newPrice - origPrice) > 0.001;

      const origCost = qty * origCov * origPrice;
      const newCost = qty * currentCov * newPrice;
      const materialDelta = newCost - origCost;

      if (Math.abs(materialDelta) > 0.01 || sizeChanged) {
        changes.push({
          type: sizeChanged ? "spec_change" : "vendor_swap",
          description: item.costCodeDescription || "Material",
          uom: item.uom,
          qty,
          originalSpec: `${origSize} @ $${origPrice.toFixed(2)}`,
          originalCov: origCov.toFixed(2),
          newSpec: `${item.size} @ $${newPrice.toFixed(2)}`,
          newCov: currentCov.toFixed(2),
          origCost,
          newCost,
          delta: materialDelta,
          sizeChanged,
          priceChanged,
          laborId: item.laborId,
        });
        totalMaterialDelta += materialDelta;
      }

      if (sizeChanged && item.laborId && origCov > 0 && currentCov > 0) {
        const coverageDelta = qty * currentCov - qty * origCov;
        if (Math.abs(coverageDelta) > 0.01) {
          laborCoverageChanges.push({
            materialLineItemId: item.lineItemId,
            assemblyId: item.assembly,
            laborId: item.laborId,
            description: item.costCodeDescription || "Material",
            coverageDelta,
          });
        }
      }
    });

    const finalLaborChanges = [];
    laborCoverageChanges.forEach((change) => {
      const matching = linkedLaborItems.filter(
        (l) =>
          l.assemblyId?.toString() === change.assemblyId?.toString() &&
          l.laborId === change.laborId,
      );
      matching.forEach((labor) => {
        const baseRate = safeNumber(labor.unitRate, 0);
        const rate = laborRateOverrides[labor.lineItemId] ?? baseRate;
        const key = `${change.materialLineItemId}_${labor.lineItemId}`;
        const delta = laborDeltaOverrides[key] ?? change.coverageDelta;
        const cost = delta * rate;
        if (Math.abs(cost) > 0.01) {
          finalLaborChanges.push({
            materialLineItemId: change.materialLineItemId,
            laborLineItemId: labor.lineItemId,
            laborId: labor.laborId,
            assemblyId: labor.assemblyId,
            description:
              labor.costCodeDescription || labor.itemDescription || "Labor",
            uom: labor.uom,
            coverageDelta: delta,
            unitRate: rate,
            deltaCost: cost,
          });
        }
      });
    });

    const totalLaborDelta = finalLaborChanges.reduce(
      (s, lc) => s + lc.deltaCost,
      0,
    );
    return {
      changes,
      laborChanges: finalLaborChanges,
      totalMaterialDelta,
      totalLaborDelta,
      totalDelta: totalMaterialDelta + totalLaborDelta,
      hasChanges: changes.length > 0 || finalLaborChanges.length > 0,
    };
  }, [
    order,
    editableItems,
    linkedLaborItems,
    laborRateOverrides,
    laborDeltaOverrides,
  ]);

  // ─── Grouped items ───────────────────────────────────────────────
  const groupedItems = useMemo(() => {
    const map = new Map();
    editableItems.forEach((item) => {
      const key = `${item.materialCode}|||${item.costCodeDescription}|||${item.size}|||${item.price}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          description: item.costCodeDescription,
          materialCode: item.materialCode,
          size: item.size,
          uom: item.uom,
          coverage: getCoverage(item),
          origPrice: item.stockOutOriginalVendorPrice || 0,
          newPrice: item.price || 0,
          totalQty: 0,
          conditions: new Set(),
          bidAreas: new Set(),
          originalVendorName: item.originalVendorName,
          isInventorySource: item.isInventorySource,
          _items: [],
        });
      }
      const g = map.get(key);
      g.totalQty += item.quantity || 0;
      if (item.conditionName) g.conditions.add(item.conditionName);
      if (item.bidArea) g.bidAreas.add(item.bidArea);
      g._items.push(item);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.materialCode || a.description || "").localeCompare(
        b.materialCode || b.description || "",
      ),
    );
  }, [editableItems]);

  // ─── Totals ──────────────────────────────────────────────────────
  const orderTotal = useMemo(
    () =>
      editableItems.reduce(
        (s, i) => s + (i.quantity || 0) * getCoverage(i) * (i.price || 0),
        0,
      ),
    [editableItems],
  );

  const surchargesTotal = useMemo(
    () => surcharges.reduce((s, sc) => s + safeNumber(sc.price, 0), 0),
    [surcharges],
  );

  const originalTotal = useMemo(
    () =>
      editableItems.reduce(
        (s, i) =>
          s +
          (i.quantity || 0) *
            (i.originalCoverage || getCoverage(i)) *
            (i.stockOutOriginalVendorPrice || 0),
        0,
      ),
    [editableItems],
  );
  const varianceAmount = orderTotal - originalTotal;

  // ─── Helpers ─────────────────────────────────────────────────────
  const formatCurrency = (n) =>
    Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      : "-";
  const statusColors = {
    pending: "bg-yellow-600/20 text-yellow-400 border-yellow-500/30",
    confirmed: "bg-blue-600/20 text-blue-400 border-blue-500/30",
    delivered: "bg-green-600/20 text-green-400 border-green-500/30",
    void: "bg-red-600/20 text-red-400 border-red-500/30",
  };

  // ─── Deliver ─────────────────────────────────────────────────────
  const handleDeliver = async () => {
    if (varianceSummary.hasChanges && !varianceReasonCategory.trim()) {
      setDialog({
        open: true,
        type: "error",
        title: "Variance Reason Required",
        message: "Please select a reason category for the variances.",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.put(
        `${API_BASE}/stock-out-orders/${orderId}/deliver`,
        {
          deliveredAt: new Date().toISOString(),
          varianceReason: varianceReason,
          varianceReasonCategory: varianceReasonCategory,
          stockOutReason: varianceReason, // keep in sync
          stockOutReasonCategory: varianceReasonCategory, // ADD THIS
          items: editableItems.map((i) => ({
            assembly: i.assembly,
            lineItemId: i.lineItemId,
            quantity: i.quantity,
            size: i.size,
            price: i.price,
            bidArea: i.bidArea,
            coveragePerUnit: getCoverage(i),
            originalPrice: i.stockOutOriginalVendorPrice,
            isInventorySource: i.isInventorySource,
            inventoryItemId: i.inventoryItemId,
          })),
        },
        { headers: getHeaders() },
      );
      setDialog({
        open: true,
        type: "success",
        title: "Delivered",
        message: res.data.hasVariance
          ? `Delivered. Variance: $${formatCurrency(res.data.varianceBudget?.grandTotal || 0)}`
          : order?.isInventorySource
            ? "Delivered from warehouse inventory. Stock has been deducted."
            : "Delivered successfully.",
        onConfirm: () => {
          setDialog((d) => ({ ...d, open: false }));
          window.location.reload();
        },
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showDeliverConfirmation = () => {
    setDialog({
      open: true,
      type: "warning",
      title: "Confirm Delivery",
      message: order?.isInventorySource
        ? "This will:\n• Release pending from original items\n• Deduct quantities from warehouse inventory\n\nCannot be undone."
        : "This will:\n• Release pending from original items\n• Create variance budget\n• Mark new vendor items as ordered\n\nCannot be undone.",
      confirmText: "Deliver",
      cancelText: "Cancel",
      onConfirm: () => {
        setDialog((d) => ({ ...d, open: false }));
        handleDeliver();
      },
      onCancel: () => setDialog((d) => ({ ...d, open: false })),
      onClose: () => setDialog((d) => ({ ...d, open: false })),
    });
  };

  // ─── Void ────────────────────────────────────────────────────────
  const handleVoid = async () => {
    setIsSubmitting(true);
    try {
      await axios.put(
        `${API_BASE}/stock-out-orders/${orderId}/void`,
        { voidReason },
        { headers: getHeaders() },
      );
      setShowVoidModal(false);
      setDialog({
        open: true,
        type: "success",
        title: "Voided",
        message: "Pending quantities restored.",
        onConfirm: () => {
          setDialog((d) => ({ ...d, open: false }));
          window.location.reload();
        },
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Failed",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Multi-token search ───────────────────────────────────────────────────────

  function normaliseSearch(str) {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .replace(/[''"""]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function buildSOSearchBlob(item) {
    const parts = [
      item.materialCode || "",
      item.costCodeDescription || "",
      item.conditionName || "",
      item.sectionNumber || "",
      item.size || "",
      item.uom || "",
      item.bidArea || "",
      item.laborId || "",
      item.originalVendorName || "",
    ];
    return normaliseSearch(parts.join("  "));
  }

  function soTokenVariants(token) {
    const variants = [token];
    const fractionCondensed = token.match(/^(\d)(\d)(\d)$/);
    if (fractionCondensed) {
      const [, a, b, c] = fractionCondensed;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
    }
    const fractionCondensed4 = token.match(/^(\d)(\d)(\d{2})$/);
    if (fractionCondensed4) {
      const [, a, b, c] = fractionCondensed4;
      variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`);
    }
    const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d+)$/);
    if (fractionExpanded) {
      const [, a, b, c] = fractionExpanded;
      variants.push(`${a}${b}${c}`);
    }
    const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
    if (gaugeMatch) {
      const [, num] = gaugeMatch;
      variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
    }
    return variants;
  }

  function soScoreItem(item, tokens) {
    let score = 0;
    const fullQuery = tokens.join(" ");
    const code = normaliseSearch(item.materialCode);
    const desc = normaliseSearch(item.costCodeDescription);
    const cond = normaliseSearch(item.conditionName);
    const size = normaliseSearch(item.size);

    if (code === fullQuery) score += 10000;
    else if (code.startsWith(fullQuery)) score += 5000;
    else if (code.includes(fullQuery)) score += 2000;

    if (desc === fullQuery) score += 8000;
    else if (desc.startsWith(fullQuery)) score += 3000;

    const fields = [
      { text: code, weight: 50 },
      { text: desc, weight: 30 },
      { text: cond, weight: 15 },
      { text: size, weight: 12 },
      { text: normaliseSearch(item.sectionNumber), weight: 10 },
      { text: normaliseSearch(item.laborId), weight: 8 },
      { text: normaliseSearch(item.originalVendorName), weight: 5 },
      { text: normaliseSearch(item.uom), weight: 4 },
    ];

    for (const token of tokens) {
      const variants = soTokenVariants(token);
      for (const { text, weight } of fields) {
        if (!text) continue;
        for (const v of variants) {
          if (text === v) {
            score += weight * 3;
            break;
          } else if (text.startsWith(v)) {
            score += weight * 2;
            break;
          } else if (text.includes(v)) {
            score += weight;
            break;
          }
        }
      }
    }

    score -= desc.length * 0.01;
    return score;
  }

  function soMultiTokenSearch(items, searchStr) {
    const raw = normaliseSearch(searchStr);
    if (!raw) return items;
    const tokens = raw.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return items;

    const matched = items.filter((item) => {
      const blob = buildSOSearchBlob(item);
      return tokens.every((token) =>
        soTokenVariants(token).some((v) => blob.includes(v)),
      );
    });

    return matched
      .map((item) => ({ item, score: soScoreItem(item, tokens) }))
      .sort((a, b) => b.score - a.score)
      .map((e) => e.item);
  }
  // ─────────────────────────────────────────────────────────────────────────────

  // ─── PDF ─────────────────────────────────────────────────────────
  const generatePDF = async (sendEmail = false) => {
    if (isSubmitting || !order || !project) return;
    setIsSubmitting(true);
    try {
      const liveForm = formDataRef.current;

      if (sendEmail) {
        try {
          await axios.put(
            `${API_BASE}/stock-out-orders/${orderId}`,
            {
              shippingMethod: liveForm.shippingMethod,
              deliveryInstructions: liveForm.deliveryInstructions,
              expectedDeliveryDate: liveForm.expectedDeliveryDate || null,
              fieldContact: liveForm.fieldContact,
              deliveryContact: liveForm.deliveryContact,
              contact: liveForm.contact,
              surcharges,
              varianceReason,
              varianceReasonCategory,
              items: editableItems.map((i) => ({
                assembly: i.assembly,
                lineItemId: i.lineItemId,
                quantity: i.quantity,
                size: i.size,
                price: i.price,
                coveragePerUnit: i.coveragePerUnit,
                bidArea: i.bidArea,
                stockOutNewVendorPrice: i.price,
                stockOutOriginalVendorPrice: i.stockOutOriginalVendorPrice,
              })),
            },
            { headers: getHeaders() },
          );
        } catch (saveErr) {
          console.warn("Pre-email auto-save failed:", saveErr);
        }
      }

      const [vtr, str] = await Promise.all([
        fetch("/material_order_template2.docx"),
        fetch("/material_order_template2_site.docx"),
      ]);
      if (!vtr.ok || !str.ok) throw new Error("Failed to load templates");
      const vtb = await vtr.arrayBuffer(),
        stb = await str.arrayBuffer();
      const b64r = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/,
        vb64 =
          /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      const b64p = (tv) => {
        if (typeof tv !== "string" || !b64r.test(tv)) return null;
        const s = tv.replace(b64r, "");
        if (!vb64.test(s)) throw new Error("bad b64");
        const b = window.atob(s),
          u = new Uint8Array(b.length);
        for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i);
        return u.buffer;
      };
      let img = null;
      if (companyProfile?.logo) {
        if (companyProfile.logo.startsWith("data:image"))
          img = companyProfile.logo;
        else {
          try {
            const r = await fetch(companyProfile.logo);
            if (r.ok) {
              const bl = await r.blob();
              img = await new Promise((res) => {
                const rd = new FileReader();
                rd.onloadend = () => res(rd.result);
                rd.readAsDataURL(bl);
              });
            }
          } catch {}
        }
      }
      const io = { getImage: b64p, getSize: () => [125, 90] };
      const v =
        vendors.find((x) => x._id === (order.vendor?._id || order.vendor)) ||
        {};
      const vc = order.isInventorySource
        ? order.inventoryWarehouseName || "Warehouse Inventory"
        : contacts
            .filter((c) => liveForm.contact.includes(c._id))
            .map((c) => `${c.firstName} ${c.lastName} (${c.email})`)
            .join(", ") || "N/A";
      const fc =
        users
          .filter((u) => liveForm.fieldContact.includes(u._id))
          .map((u) => `${u.displayName || "User"} (${u.phoneNumber || "N/A"})`)
          .join(", ") || "N/A";
      const pl = project?.address
        ? [
            project.address.street,
            project.address.city,
            `${project.address.state} ${project.address.postalCode}`,
          ]
            .filter((p) => p?.trim())
            .join(", ") || "N/A"
        : "N/A";
      const sd = {
        companyName: companyProfile?.companyName || "",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}`
          : "N/A",
        companyPhone: companyProfile?.phone || "N/A",
        projectName: project?.projectName || "",
        projectLocation: pl,
        vendorName: order.isInventorySource
          ? order.inventoryWarehouseName || "Warehouse Inventory"
          : v.companyName || "",
        vendorAddress: order.isInventorySource
          ? "Internal Stock"
          : v.address
            ? `${v.address.street}, ${v.address.city}, ${v.address.state} ${v.address.postalCode}`
            : "N/A",
        vendorContacts: vc,
        fieldContacts: fc,
        deliveryContact: "N/A",
        materialOrderNumber: `${order.materialOrderNumber}`,
        poNumber: `${project.projectNumber}`,
        createdAt: order.createdAt
          ? new Date(order.createdAt).toLocaleDateString("en-US", {
              timeZone: "UTC",
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "N/A",
        expectedDeliveryDate: liveForm.expectedDeliveryDate
          ? new Date(liveForm.expectedDeliveryDate).toLocaleDateString(
              "en-US",
              {
                timeZone: "UTC",
                year: "numeric",
                month: "long",
                day: "numeric",
              },
            )
          : "N/A",
        shippingMethod: liveForm.shippingMethod || "Delivery",
        deliveryInstructions: liveForm.deliveryInstructions || "N/A",
        revision: order.status === "delivered" ? "FINAL" : "",
        image: img,
      };
      const gsp = async (tb, td, fl) => {
        const z = new PizZip(tb),
          d = new Docxtemplater(z, {
            paragraphLoop: true,
            linebreaks: true,
            modules: [new ImageModule(io)],
          });
        d.setData(td);
        d.render();
        const db = d.getZip().generate({ type: "blob" });
        const fd = new FormData();
        fd.append(
          "docx",
          db,
          `MO_${order.poNumber}-${order.materialOrderNumber}_${fl}.docx`,
        );
        fd.append("documentType", "MaterialOrder");
        fd.append(
          "identifier",
          `MO_${order.poNumber}-${order.materialOrderNumber}_${fl}`,
        );
        const pr = await axios.post(`${API_BASE}/upload/convert-to-pdf`, fd, {
          headers: getHeaders(),
          responseType: "blob",
        });
        return new Blob([pr.data], { type: "application/pdf" });
      };
      const pi = groupedItems.map((g) => {
        let du = g.uom;
        const up = (g.uom || "").toUpperCase();
        if (
          ["LF", "SF"].includes(up) ||
          up.includes("LINEAR") ||
          up.includes("SQUARE")
        )
          du = "PC";
        return {
          description:
            `${g.materialCode ? g.materialCode + " – " : ""}${g.description}`.trim(),
          size: g.size || "-",
          uom: du,
          quantity: g.totalQty.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          }),
          unitPrice: `$${g.newPrice.toFixed(2)}`,
          lineTotal: `$${(g.totalQty * g.coverage * g.newPrice).toLocaleString(
            "en-US",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          )}`,
        };
      });

      surcharges.forEach((s) => {
        pi.push({
          description: s.description || "Surcharge",
          size: "-",
          uom: "-",
          quantity: "1",
          unitPrice: `$${safeNumber(s.price, 0).toFixed(2)}`,
          lineTotal: `$${formatCurrency(safeNumber(s.price, 0))}`,
        });
      });

      const si = groupedItems.map((g) => {
        let du = g.uom;
        const up = (g.uom || "").toUpperCase();
        if (
          ["LF", "SF"].includes(up) ||
          up.includes("LINEAR") ||
          up.includes("SQUARE")
        )
          du = "PC";
        return {
          description:
            `${g.materialCode ? g.materialCode + " – " : ""}${g.description}`.trim(),
          size: g.size || "-",
          uom: du,
          quantity: g.totalQty.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          }),
          unitPrice: "",
          lineTotal: "",
        };
      });

      let taxRate = 0;
      if (
        companyProfile?.plugins?.quickbooks?.connected &&
        companyProfile?.defaultTax?.taxRate
      ) {
        taxRate = parseFloat(companyProfile.defaultTax.taxRate) || 0;
      } else if (companyProfile?.defaultTax?.taxRate) {
        taxRate = parseFloat(companyProfile.defaultTax.taxRate) || 0;
      }

      const subtotal = orderTotal + surchargesTotal;
      const tax = subtotal * (taxRate / 100);
      const grandTotal = subtotal + tax;

      const vd = {
        ...sd,
        items: pi,
        subtotal: `$${formatCurrency(subtotal)}`,
        taxRate: taxRate > 0 ? `${taxRate}%` : "0%",
        taxAmount: `$${formatCurrency(tax)}`,
        grandTotal: `$${formatCurrency(grandTotal)}`,
      };
      const sdd = {
        ...sd,
        items: si,
        subtotal: "",
        taxRate: "",
        taxAmount: "",
        grandTotal: "",
      };
      const vp = await gsp(vtb, vd, "VENDOR"),
        sp = await gsp(stb, sdd, "SITE");
      if (sendEmail) {
        setVendorPdfBuffer(vp);
        setSitePdfBuffer(sp);
        const liveContacts = contacts.filter((c) =>
          liveForm.contact.includes(c._id),
        );
        const livePc = liveContacts[0];
        const liveEmails = liveContacts
          .map((c) => c.email)
          .filter(Boolean)
          .join(", ");
        setEmailForm((prev) => ({
          ...prev,
          to: liveEmails || prev.to,
          body: `${livePc?.firstName ? `Hi ${livePc.firstName},` : "Dear Vendor,"}\n\nPlease find attached the material order ${order.materialOrderNumber} for your review and action.${liveForm.deliveryInstructions ? `\n\nDelivery Instructions: ${liveForm.deliveryInstructions}` : ""}\n\nBest regards,`,
        }));
        setShowEmailForm(true);
      } else {
        saveAs(vp, `MaterialOrder_${order.materialOrderNumber}.pdf`);
        setTimeout(
          () =>
            saveAs(sp, `MaterialOrder_${order.materialOrderNumber}_SITE.pdf`),
          500,
        );
        setDialog({
          open: true,
          type: "success",
          title: "Downloaded",
          message: "PDFs downloaded!",
          onConfirm: () => setDialog((d) => ({ ...d, open: false })),
        });
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.message,
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject) {
        setDialog({
          open: true,
          type: "error",
          title: "Missing",
          message: "Fill in email fields",
          onConfirm: () => setDialog((d) => ({ ...d, open: false })),
        });
        return;
      }
      if (!(vendorPdfBuffer instanceof Blob)) {
        setDialog({
          open: true,
          type: "error",
          title: "Missing PDF",
          message: "Regenerate PDF",
          onConfirm: () => setDialog((d) => ({ ...d, open: false })),
        });
        return;
      }

      const n = (project?.projectName || "Project")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .trim();
      const fd = new FormData();
      fd.append("to", emailForm.to);
      fd.append("subject", emailForm.subject);
      fd.append("body", emailForm.body);
      fd.append("signature", emailForm.signature || "");
      fd.append("from", userEmail);
      fd.append(
        "files",
        vendorPdfBuffer,
        `MaterialOrder-${order.materialOrderNumber} - ${n}.pdf`,
      );
      if (sitePdfBuffer instanceof Blob)
        fd.append(
          "siteFiles",
          sitePdfBuffer,
          `MaterialOrder-${order.materialOrderNumber} - ${n} - SITE.pdf`,
        );
      await axios.post(
        `${API_BASE}/materialOrders2/${orderId}/send-email`,
        fd,
        { headers: getHeaders() },
      );
      setShowEmailForm(false);
      setVendorPdfBuffer(null);
      setSitePdfBuffer(null);
      setDialog({
        open: true,
        type: "success",
        title: "Sent",
        message: "Email sent!",
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message,
        onConfirm: () => setDialog((d) => ({ ...d, open: false })),
      });
    }
  };

  // ─── Loading / Error ─────────────────────────────────────────────
  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-200">Loading...</div>
      </div>
    );
  if (error || !order)
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <div className="text-red-400">{error || "Not found"}</div>
        <button
          onClick={() =>
            navigate(`/Projects/${projectId}`, {
              state: { initialTab: "Materials" },
            })
          }
          className="px-4 py-2 text-sm text-white bg-gray-600 rounded-md hover:bg-gray-500"
        >
          Back
        </button>
      </div>
    );

  const isPending = order.status === "pending" || order.status === "confirmed";
  const isDelivered = order.status === "delivered";
  const isVoid = order.status === "void";
  const isInventoryOrder = !!order.isInventorySource;

  // Inventory items lock size + price editing
  const canEditItem = (item) =>
    isPending && !item.isInventorySource && !item.inventoryItemId;

  return (
    <div className="ml-4 mx-auto px-4 py-6 container">
      <AlertDialog
        open={dialog.open}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText || "OK"}
        cancelText={dialog.cancelText || "Cancel"}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      <div className="flex gap-6">
        {/* ═══ MAIN CONTENT ═══ */}
        <div
          className={`flex-1 ${showVariancePanel && varianceSummary.hasChanges && isPending && !isInventoryOrder ? "max-w-[70%]" : ""}`}
        >
          {/* HEADER */}
          <div className="mb-6 bg-gray-700 rounded-lg p-6 border border-gray-600 shadow-xl">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-semibold text-white">
                    Stock-Out #{project.projectNumber}-
                    {order.materialOrderNumber}
                  </h1>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusColors[order.status] || statusColors.pending}`}
                  >
                    {order.status?.charAt(0).toUpperCase() +
                      order.status?.slice(1)}
                  </span>
                  {isInventoryOrder && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-300 border-green-500/30">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      Warehouse
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400">
                  {isInventoryOrder
                    ? "Warehouse fulfillment — size and price locked."
                    : "Vendor swap — editable until delivery."}
                </p>
              </div>
              <div className="flex space-x-3">
                {!isPending && (
                  <button
                    onClick={() => generatePDF(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Download PDF
                  </button>
                )}
                <button
                  onClick={() =>
                    navigate(`/Projects/${projectId}`, {
                      state: { initialTab: "Materials" },
                    })
                  }
                  className="px-4 py-2 text-sm text-white bg-gray-600 rounded-md hover:bg-gray-500"
                >
                  ← Back
                </button>
              </div>
            </div>
          </div>

          {/* INFO GRID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* ── Card 1: Source info (vendor swap OR warehouse) ── */}
            <div className="bg-gray-700 rounded-lg p-5 border border-gray-600 shadow-xl">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {isInventoryOrder ? "Inventory Source" : "Vendor Swap"}
              </h2>
              <div className="space-y-3">
                {isInventoryOrder ? (
                  <>
                    <div>
                      <span className="text-xs text-gray-500">Source</span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          Warehouse Inventory
                        </span>
                      </div>
                      <p className="text-sm text-green-400 font-medium mt-1">
                        {order.inventoryWarehouseName || "Internal Warehouse"}
                      </p>
                    </div>
                    <div className="pt-2 border-t border-gray-600">
                      <p className="text-xs text-gray-400">
                        Items fulfilled from warehouse stock. No cost variance
                        budget is generated on delivery.
                      </p>
                    </div>
                    {order.stockOutReason && (
                      <div className="pt-2 border-t border-gray-600">
                        <span className="text-xs text-gray-500">Reason</span>
                        <p className="text-sm text-gray-300">
                          {order.stockOutReason}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <span className="text-xs text-gray-500">
                        Original Vendor
                      </span>
                      <p className="text-sm text-red-400 font-medium">
                        {order.stockOutOriginalVendor?.companyName || "Unknown"}
                      </p>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                      <span className="text-xs">swapped to</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">New Vendor</span>
                      <p className="text-sm text-green-400 font-medium">
                        {order.vendor?.companyName || "Unknown"}
                      </p>
                    </div>
                    {order.stockOutReason && (
                      <div className="pt-2 border-t border-gray-600">
                        <span className="text-xs text-gray-500">Reason</span>
                        <p className="text-sm text-gray-300">
                          {order.stockOutReason}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Card 2: Order details ── */}
            <div className="bg-gray-700 rounded-lg p-5 border border-gray-600 shadow-xl">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Order Details
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">PO</span>
                  <span className="text-white">
                    {order.purchaseOrder?.title || order.poNumber}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-gray-400 text-xs uppercase">
                    Shipping
                  </span>
                  {isPending ? (
                    <select
                      value={formData.shippingMethod}
                      onChange={(e) =>
                        handleFormChange("shippingMethod", e.target.value)
                      }
                      className="w-full px-2 py-1.5 text-sm rounded border border-gray-500 bg-gray-600 text-gray-200"
                    >
                      <option value="Delivery">Delivery</option>
                      <option value="Pickup">Pickup</option>
                    </select>
                  ) : (
                    <span className="text-white">
                      {order.shippingMethod || "Delivery"}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-gray-400 text-xs uppercase">
                    Expected Delivery
                  </span>
                  {isPending ? (
                    <input
                      type="date"
                      value={formData.expectedDeliveryDate}
                      onChange={(e) =>
                        handleFormChange("expectedDeliveryDate", e.target.value)
                      }
                      className="w-full px-2 py-1 text-sm rounded border border-gray-500 bg-gray-600 text-gray-200"
                    />
                  ) : (
                    <span className="text-white">
                      {formData.expectedDeliveryDate || "Not set"}
                    </span>
                  )}
                </div>
                {/* Add this block after the Expected Delivery div */}
                <div className="flex flex-col gap-1">
                  <span className="text-gray-400 text-xs uppercase">
                    Delivery Instructions
                  </span>
                  {isPending ? (
                    <textarea
                      value={formData.deliveryInstructions}
                      onChange={(e) =>
                        handleFormChange("deliveryInstructions", e.target.value)
                      }
                      rows={2}
                      className="w-full px-2 py-1 text-sm rounded border border-gray-500 bg-gray-600 text-gray-200 resize-none"
                      placeholder="Enter any special instructions..."
                    />
                  ) : (
                    <span className="text-white text-xs">
                      {order.deliveryInstructions || "None"}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-gray-400 text-xs uppercase">
                    Field Contacts
                  </span>
                  {isPending ? (
                    <Select
                      options={userOptions}
                      value={userOptions.filter((o) =>
                        formData.fieldContact.includes(o.value),
                      )}
                      onChange={(selected) =>
                        handleFormChange(
                          "fieldContact",
                          selected ? selected.map((o) => o.value) : [],
                        )
                      }
                      isMulti
                      placeholder="Select..."
                      styles={multiSelectFilterStyles}
                    />
                  ) : (
                    <span className="text-white text-xs">
                      {users
                        .filter((u) => formData.fieldContact.includes(u._id))
                        .map((u) => u.displayName || "User")
                        .join(", ") || "None"}
                    </span>
                  )}
                </div>
                {/* Only show vendor contacts for non-inventory orders */}
                {!isInventoryOrder && (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-400 text-xs uppercase">
                      Vendor Contacts
                    </span>
                    {isPending ? (
                      <Select
                        options={contactOptions}
                        value={contactOptions.filter((o) =>
                          formData.contact.includes(o.value),
                        )}
                        onChange={(selected) =>
                          handleFormChange(
                            "contact",
                            selected ? selected.map((o) => o.value) : [],
                          )
                        }
                        isMulti
                        placeholder="Select vendor contacts..."
                        styles={multiSelectFilterStyles}
                      />
                    ) : (
                      <span className="text-white text-xs">
                        {contacts
                          .filter((c) => formData.contact.includes(c._id))
                          .map((c) => `${c.firstName} ${c.lastName}`)
                          .join(", ") || "None"}
                      </span>
                    )}
                  </div>
                )}
                {formData.shippingMethod === "Pickup" && (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-400 text-xs uppercase">
                      Delivery Driver
                    </span>
                    {isPending ? (
                      <Select
                        options={userOptions}
                        value={
                          userOptions.find(
                            (o) => o.value === formData.deliveryContact,
                          ) || null
                        }
                        onChange={(opt) =>
                          handleFormChange(
                            "deliveryContact",
                            opt?.value || null,
                          )
                        }
                        placeholder="Select Driver..."
                        styles={multiSelectFilterStyles}
                        isClearable
                      />
                    ) : (
                      <span className="text-white text-xs">
                        {users.find((u) => u._id === formData.deliveryContact)
                          ?.displayName || "None"}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Created</span>
                  <span className="text-white">
                    {formatDate(order.createdAt)}
                  </span>
                </div>
                {isDelivered && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Delivered</span>
                    <span className="text-green-400">
                      {formatDate(order.deliveredAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Card 3: Financial summary ── */}
            {userRole !== "foreman" && (
              <div className="bg-gray-700 rounded-lg p-5 border border-gray-600 shadow-xl">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Financial Summary
                </h2>
                {isInventoryOrder ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">
                        Warehouse Cost
                      </span>
                      <span className="text-lg font-semibold text-green-400">
                        ${formatCurrency(orderTotal)}
                      </span>
                    </div>
                    {surchargesTotal !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">
                          Surcharges
                        </span>
                        <span className="text-sm text-gray-300">
                          ${formatCurrency(surchargesTotal)}
                        </span>
                      </div>
                    )}
                    {surchargesTotal !== 0 && (
                      <div className="border-t border-gray-600 pt-2 flex justify-between">
                        <span className="text-sm text-gray-400">Total</span>
                        <span className="text-lg font-semibold text-green-400">
                          ${formatCurrency(orderTotal + surchargesTotal)}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-gray-600 pt-2">
                      <p className="text-xs text-gray-500">
                        No variance budget generated — items use internal
                        warehouse cost.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">
                        Original Cost
                      </span>
                      <span className="text-sm text-gray-300">
                        ${formatCurrency(originalTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-400">
                        New Vendor Cost
                      </span>
                      <span className="text-lg font-semibold text-orange-400">
                        ${formatCurrency(orderTotal)}
                      </span>
                    </div>
                    {surchargesTotal !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">
                          Surcharges
                        </span>
                        <span className="text-sm text-gray-300">
                          ${formatCurrency(surchargesTotal)}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-gray-600 pt-2 flex justify-between">
                      <span className="text-sm text-gray-400">Variance</span>
                      <span
                        className={`text-sm font-medium ${
                          varianceAmount > 0.01
                            ? "text-red-400"
                            : varianceAmount < -0.01
                              ? "text-green-400"
                              : "text-gray-400"
                        }`}
                      >
                        {varianceAmount > 0 ? "+" : ""}$
                        {formatCurrency(varianceAmount)}
                      </span>
                    </div>
                    {surchargesTotal !== 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-400">
                          Grand Total
                        </span>
                        <span className="text-lg font-semibold text-orange-400">
                          ${formatCurrency(orderTotal + surchargesTotal)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* BANNERS */}
          {isVoid && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <span className="text-red-400 font-medium">
                This order has been voided.
              </span>
              {order.voidReason && (
                <p className="text-sm text-gray-400 mt-1">
                  Reason: {order.voidReason}
                </p>
              )}
            </div>
          )}
          {isDelivered && (
            <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <span className="text-green-400 font-medium">
                {isInventoryOrder
                  ? "Delivered from warehouse inventory. Stock has been deducted."
                  : "Delivered. Vendor swap is complete."}
              </span>
            </div>
          )}

          {/* ITEMS TABLE */}
          <div className="bg-gray-700 rounded-lg border border-gray-600 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-600 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Order Items</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">
                  {groupByItem
                    ? `${filteredGroupedItems.length} grouped`
                    : `${filteredEditableItems.length} lines`}
                </span>
                <label className="text-sm text-gray-300">Group</label>
                <div
                  className={`relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full transition-colors ${groupByItem ? "bg-orange-600" : "bg-gray-600"}`}
                  onClick={() => setGroupByItem(!groupByItem)}
                >
                  <span
                    className={`absolute left-1 top-1 block h-4 w-4 rounded-full bg-white transition-transform ${groupByItem ? "translate-x-5" : "translate-x-0"}`}
                  />
                </div>
              </div>
            </div>

            {/* Filter bar */}
            <div className="bg-gray-800 rounded-lg p-3 mx-2 mt-2 mb-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-4 text-xl">
                  <span className="text-gray-400">
                    <span className="text-white font-semibold">
                      {groupByItem
                        ? filteredGroupedItems.length
                        : filteredEditableItems.length}
                    </span>
                    {filteredEditableItems.length !== editableItems.length && (
                      <span className="text-gray-500">
                        {" "}
                        / {editableItems.length}
                      </span>
                    )}{" "}
                    items
                  </span>
                  <span className="text-gray-600">|</span>
                  <span className="text-orange-400 font-medium">
                    ${formatCurrency(filteredTotals.itemsTotal)}
                  </span>
                  <span className="text-gray-600">|</span>
                  <span className="text-blue-400">
                    {filteredTotals.totalQuantity} qty
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <input
                    type="text"
                    value={tableFilters.descriptionSearch}
                    onChange={(e) =>
                      handleTableFilterChange(
                        "descriptionSearch",
                        e.target.value,
                      )
                    }
                    placeholder="Search..."
                    className="w-40 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                  />
                  {tableFilters.descriptionSearch && (
                    <button
                      onClick={() =>
                        handleTableFilterChange("descriptionSearch", "")
                      }
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      ×
                    </button>
                  )}
                </div>
                <span className="text-gray-600">|</span>
                {tableFilters.conditionName.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-teal-600/80 text-white rounded">
                    Condition:{" "}
                    {tableFilters.conditionName.length === 1
                      ? tableFilters.conditionName[0].substring(0, 15)
                      : `${tableFilters.conditionName.length} selected`}
                    <button
                      onClick={() =>
                        handleTableFilterChange("conditionName", [])
                      }
                      className="hover:text-teal-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {tableFilters.sectionNumber.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-purple-600/80 text-white rounded">
                    Section:{" "}
                    {tableFilters.sectionNumber.length === 1
                      ? tableFilters.sectionNumber[0]
                      : `${tableFilters.sectionNumber.length} selected`}
                    <button
                      onClick={() =>
                        handleTableFilterChange("sectionNumber", [])
                      }
                      className="hover:text-purple-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {tableFilters.size.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-cyan-600/80 text-white rounded">
                    Size:{" "}
                    {tableFilters.size.length === 1
                      ? tableFilters.size[0]
                      : `${tableFilters.size.length} selected`}
                    <button
                      onClick={() => handleTableFilterChange("size", [])}
                      className="hover:text-cyan-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {tableFilters.uom.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-indigo-600/80 text-white rounded">
                    UOM:{" "}
                    {tableFilters.uom.length === 1
                      ? tableFilters.uom[0]
                      : `${tableFilters.uom.length} selected`}
                    <button
                      onClick={() => handleTableFilterChange("uom", [])}
                      className="hover:text-indigo-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {tableFilters.bidArea.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-green-600/80 text-white rounded">
                    Bid Area:{" "}
                    {tableFilters.bidArea.length === 1
                      ? tableFilters.bidArea[0]
                      : `${tableFilters.bidArea.length} selected`}
                    <button
                      onClick={() => handleTableFilterChange("bidArea", [])}
                      className="hover:text-green-200"
                    >
                      ×
                    </button>
                  </span>
                )}
                {hasActiveFilters && (
                  <button
                    onClick={clearAllTableFilters}
                    className="px-1.5 py-0.5 text-[11px] font-medium text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <thead className="bg-gray-800">
                  <tr>
                    {/* Condition - filterable */}
                    <th
                      className={`px-4 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${tableFilters.conditionName.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                      onClick={() => toggleTableFilter("conditionName")}
                    >
                      <span className="flex items-center gap-0.5">
                        {groupByItem ? "Item" : "Condition"}
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {tableFilters.conditionName.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {tableFilters.conditionName.length}
                          </span>
                        )}
                      </span>
                      {showTableFilter === "conditionName" && (
                        <div
                          ref={tableConditionRef}
                          className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                          style={{ minWidth: "300px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-3">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={tableFilterOptions.conditionName.map(
                                (v) => ({ value: v, label: v }),
                              )}
                              value={tableFilterOptions.conditionName
                                .map((v) => ({ value: v, label: v }))
                                .filter((o) =>
                                  tableFilters.conditionName.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleTableFilterChange(
                                  "conditionName",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Description
                    </th>
                    {/* Size - filterable */}
                    <th
                      className={`px-4 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${tableFilters.size.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                      onClick={() => toggleTableFilter("size")}
                    >
                      <span className="flex items-center gap-0.5">
                        Size
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {tableFilters.size.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {tableFilters.size.length}
                          </span>
                        )}
                      </span>
                      {showTableFilter === "size" && (
                        <div
                          ref={tableSizeRef}
                          className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                          style={{ minWidth: "220px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-3">
                            {tableFilterOptions.sizeOptions.some(
                              (o) => o.inFilter,
                            ) && (
                              <div className="mb-1 px-1 py-0.5 text-[10px] font-semibold text-orange-400 uppercase tracking-wide">
                                In current filter
                              </div>
                            )}
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={tableFilterOptions.sizeOptions}
                              formatOptionLabel={(opt) => {
                                const inFilter =
                                  tableFilterOptions.sizeOptions.find(
                                    (o) => o.value === opt.value,
                                  )?.inFilter;
                                return (
                                  <div className="flex items-center gap-2">
                                    {inFilter && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                                    )}
                                    <span>{opt.label}</span>
                                  </div>
                                );
                              }}
                              value={tableFilterOptions.sizeOptions.filter(
                                (o) => tableFilters.size.includes(o.value),
                              )}
                              onChange={(opts) =>
                                handleTableFilterChange(
                                  "size",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                ...multiSelectFilterStyles,
                                option: (base, state) => {
                                  const opts = tableFilterOptions.sizeOptions;
                                  const idx = opts.findIndex(
                                    (o) => o.value === state.data.value,
                                  );
                                  const isSeparator =
                                    idx > 0 &&
                                    !opts[idx].inFilter &&
                                    opts[idx - 1]?.inFilter;
                                  return {
                                    ...base,
                                    backgroundColor: state.isFocused
                                      ? "#4b5563"
                                      : "#374151",
                                    color: state.isSelected
                                      ? "#f97316"
                                      : "#e5e7eb",
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    borderTop: isSeparator
                                      ? "1px solid #4b5563"
                                      : "none",
                                    marginTop: isSeparator ? "4px" : "0",
                                  };
                                },
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    {/* UOM - filterable */}
                    <th
                      className={`px-4 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${tableFilters.uom.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                      onClick={() => toggleTableFilter("uom")}
                    >
                      <span className="flex items-center gap-0.5">
                        UOM
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {tableFilters.uom.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {tableFilters.uom.length}
                          </span>
                        )}
                      </span>
                      {showTableFilter === "uom" && (
                        <div
                          ref={tableUomRef}
                          className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                          style={{ minWidth: "180px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-3">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={tableFilterOptions.uom.map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              value={tableFilterOptions.uom
                                .map((v) => ({ value: v, label: v }))
                                .filter((o) =>
                                  tableFilters.uom.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleTableFilterChange(
                                  "uom",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th
                      className={`px-4 py-3 text-left text-xs font-medium cursor-pointer relative hover:text-orange-300 ${tableFilters.bidArea.length > 0 ? "text-orange-400" : "text-gray-400"} uppercase`}
                      onClick={() => toggleTableFilter("bidArea")}
                    >
                      <span className="flex items-center gap-0.5">
                        Bid Area
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {tableFilters.bidArea.length > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 text-[9px] bg-orange-500 text-white rounded-full">
                            {tableFilters.bidArea.length}
                          </span>
                        )}
                      </span>
                      {showTableFilter === "bidArea" && (
                        <div
                          ref={tableBidAreaRef}
                          className="absolute left-0 top-full z-[99999] mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl"
                          style={{ minWidth: "200px" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-3">
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              hideSelectedOptions={false}
                              options={tableFilterOptions.bidArea.map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              value={tableFilterOptions.bidArea
                                .map((v) => ({ value: v, label: v }))
                                .filter((o) =>
                                  tableFilters.bidArea.includes(o.value),
                                )}
                              onChange={(opts) =>
                                handleTableFilterChange(
                                  "bidArea",
                                  opts ? opts.map((o) => o.value) : [],
                                )
                              }
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={multiSelectFilterStyles}
                            />
                          </div>
                        </div>
                      )}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Cvg
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Qty
                    </th>
                    {!groupByItem && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                        {isInventoryOrder ? "Warehouse" : "Orig Vendor"}
                      </th>
                    )}
                    {userRole !== "foreman" && (
                      <>
                        {!isInventoryOrder && (
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                            Orig Rate
                          </th>
                        )}
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                          {isInventoryOrder ? "Cost" : "New Rate"}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                          Line Total
                        </th>
                      </>
                    )}
                    {isPending && !groupByItem && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase w-16"></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600 bg-gray-700">
                  {groupByItem
                    ? filteredGroupedItems.map((g) => {
                        const isCov = g.coverage > 1;
                        const lineTotal = g.totalQty * g.coverage * g.newPrice;
                        return (
                          <tr
                            key={g.key}
                            className={`hover:bg-gray-600 ${g.isInventorySource ? "bg-green-900/5" : ""}`}
                          >
                            <td className="px-4 py-3 text-sm text-gray-300">
                              <div>{g.materialCode || "-"}</div>
                              <div className="text-[10px] text-gray-500">
                                {g._items.length} line
                                {g._items.length > 1 ? "s" : ""}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-white max-w-[220px]">
                              {g.description || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-white">
                              {g.size || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {g.uom}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {[...g.bidAreas].filter(Boolean).join(", ") ||
                                "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-400">
                              {isCov ? g.coverage.toFixed(2) : "1"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-white font-medium">
                              {g.totalQty}
                            </td>
                            {userRole !== "foreman" && (
                              <>
                                {!isInventoryOrder && (
                                  <td className="px-4 py-3 text-sm text-right text-gray-400">
                                    ${formatCurrency(g.origPrice)}
                                    {isCov && (
                                      <div className="text-xs text-gray-500">
                                        /{g.uom}
                                      </div>
                                    )}
                                  </td>
                                )}
                                <td
                                  className={`px-4 py-3 text-sm text-right font-medium ${isInventoryOrder ? "text-green-300" : "text-white"}`}
                                >
                                  ${formatCurrency(g.newPrice)}
                                  {isCov && (
                                    <div className="text-xs text-gray-500">
                                      /{g.uom}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-right text-white">
                                  <NumericFormat
                                    value={lineTotal}
                                    displayType="text"
                                    thousandSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                  />
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })
                    : filteredEditableItems.map((item, idx) => {
                        const cov = getCoverage(item);
                        const isCov = cov > 1;
                        const isEditing =
                          isPending && editingItemId === item.lineItemId;
                        const isInventoryItem = !!(
                          item.isInventorySource || item.inventoryItemId
                        );
                        const canEdit = isPending && !isInventoryItem;
                        const sizeChanged =
                          !isInventoryItem &&
                          item.size !== item.originalSize &&
                          item.originalSize;
                        const lineTotal =
                          (item.quantity || 0) * cov * (item.price || 0);
                        return (
                          <tr
                            key={item._id || idx}
                            className={`hover:bg-gray-600 ${sizeChanged ? "bg-orange-500/5" : ""} ${isInventoryItem ? "bg-green-900/5" : ""}`}
                          >
                            <td className="px-4 py-3 text-sm text-gray-300">
                              {item.conditionName || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-white max-w-[220px]">
                              {item.costCodeDescription || "-"}
                              {item.laborId && (
                                <span className="ml-1 text-[10px] text-blue-400">
                                  [{item.laborId}]
                                </span>
                              )}
                            </td>
                            {/* Size cell — locked for inventory items */}
                            <td className="px-4 py-3 text-sm">
                              {isEditing && canEdit ? (
                                <div>
                                  <input
                                    type="text"
                                    value={item.size}
                                    onChange={(e) =>
                                      handleSizeChange(
                                        item.lineItemId,
                                        e.target.value,
                                      )
                                    }
                                    className={`w-28 px-2 py-1 text-xs rounded border bg-gray-600 text-gray-200 ${sizeChanged ? "border-orange-500" : "border-gray-500"}`}
                                    placeholder="4'x8'"
                                  />
                                  {sizeChanged && (
                                    <div className="text-[10px] text-orange-400 mt-1">
                                      was: {item.originalSize}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <span
                                    className={
                                      sizeChanged
                                        ? "text-orange-300"
                                        : "text-white"
                                    }
                                  >
                                    {item.size || "-"}
                                  </span>
                                  {isInventoryItem && (
                                    <span className="ml-1 text-[9px] text-gray-500">
                                      🔒
                                    </span>
                                  )}
                                  {sizeChanged && (
                                    <div className="text-[10px] text-orange-400">
                                      was: {item.originalSize}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {item.uom}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {item.bidArea || "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-400">
                              {isCov ? cov.toFixed(2) : "1"}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              {isEditing ? (
                                <NumericFormat
                                  value={item.quantity}
                                  onValueChange={(v) =>
                                    handleItemChange(
                                      item.lineItemId,
                                      "quantity",
                                      v.floatValue || 0,
                                    )
                                  }
                                  allowNegative={false}
                                  decimalScale={0}
                                  className="w-16 px-1.5 py-0.5 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200"
                                />
                              ) : (
                                <span className="text-white font-medium">
                                  {item.quantity || 0}
                                </span>
                              )}
                            </td>
                            {/* Source column */}
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {isInventoryItem ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-green-500/20 text-green-300 border border-green-500/30 rounded">
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16"
                                    />
                                  </svg>
                                  {order.inventoryWarehouseName || "Warehouse"}
                                </span>
                              ) : (
                                item.originalVendorName || "-"
                              )}
                            </td>
                            {userRole !== "foreman" && (
                              <>
                                {/* Orig Rate — hidden for inventory orders */}
                                {!isInventoryOrder && (
                                  <td className="px-4 py-3 text-sm text-right text-gray-400">
                                    $
                                    {formatCurrency(
                                      item.stockOutOriginalVendorPrice,
                                    )}
                                    {isCov && (
                                      <div className="text-xs text-gray-500">
                                        /{item.uom}
                                      </div>
                                    )}
                                  </td>
                                )}
                                {/* New Rate — locked for inventory items */}
                                <td className="px-4 py-3 text-sm text-right">
                                  {isEditing && canEdit ? (
                                    <NumericFormat
                                      value={item.price}
                                      onValueChange={(v) =>
                                        handleItemChange(
                                          item.lineItemId,
                                          "price",
                                          v.floatValue || 0,
                                        )
                                      }
                                      allowNegative={false}
                                      decimalScale={2}
                                      prefix="$"
                                      className="w-20 px-1.5 py-0.5 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200"
                                    />
                                  ) : (
                                    <span
                                      className={`font-medium ${isInventoryItem ? "text-green-300" : "text-white"}`}
                                    >
                                      ${formatCurrency(item.price)}
                                    </span>
                                  )}
                                  {isCov && (
                                    <div className="text-xs text-gray-500">
                                      /{item.uom}
                                      {isInventoryItem ? " · 🔒" : ""}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-right text-white">
                                  <NumericFormat
                                    value={lineTotal}
                                    displayType="text"
                                    thousandSeparator=","
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix="$"
                                  />
                                </td>
                              </>
                            )}
                            {isPending && (
                              <td className="px-4 py-3 text-center">
                                {canEdit ? (
                                  <button
                                    onClick={() =>
                                      setEditingItemId(
                                        isEditing ? null : item.lineItemId,
                                      )
                                    }
                                    className={`px-2 py-0.5 text-[10px] font-medium rounded ${isEditing ? "bg-green-500/20 text-green-400" : "bg-gray-600 text-gray-400 hover:text-white"}`}
                                  >
                                    {isEditing ? "Done" : "Edit"}
                                  </button>
                                ) : (
                                  <span className="text-[9px] text-gray-600">
                                    🔒
                                  </span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                </tbody>
                {userRole !== "foreman" && (
                  <tfoot className="bg-gray-800">
                    <tr>
                      <td
                        colSpan={
                          groupByItem
                            ? isInventoryOrder
                              ? 5
                              : 6
                            : isInventoryOrder
                              ? 6
                              : 7
                        }
                        className="px-4 py-3 text-sm text-right font-medium text-gray-400"
                      >
                        Total:
                      </td>

                      {!groupByItem && <td></td>}
                      {!isInventoryOrder && <td colSpan={1}></td>}
                      <td></td>
                      <td className="px-4 py-3 text-right">
                        <NumericFormat
                          value={filteredTotals.itemsTotal}
                          displayType="text"
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$"
                          className="text-lg font-semibold text-orange-400"
                        />
                      </td>
                      {isPending && !groupByItem && <td></td>}
                    </tr>
                  </tfoot>
                )}
              </table>
              {/* SURCHARGES */}
              <div className="px-6 py-4 border-t border-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-white">
                    Surcharges
                  </h3>
                  {!isVoid && (
                    <button
                      onClick={handleAddSurcharge}
                      className="px-2 py-1 text-xs font-medium text-gray-300 bg-gray-600 rounded hover:bg-gray-500"
                    >
                      + Add
                    </button>
                  )}
                </div>
                {surcharges.length > 0 ? (
                  <div className="space-y-1">
                    {surcharges.map((surcharge, index) => (
                      <div key={index} className="flex items-center gap-2 py-1">
                        <input
                          type="text"
                          value={surcharge.description}
                          onChange={(e) =>
                            handleSurchargeChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Description"
                          className="flex-1 px-2 py-1 text-xs rounded border-gray-500 bg-gray-600 text-gray-200"
                          disabled={isVoid}
                        />
                        <NumericFormat
                          value={surcharge.price}
                          onValueChange={(values) =>
                            handleSurchargeChange(
                              index,
                              "price",
                              values.floatValue || 0,
                            )
                          }
                          allowNegative={true}
                          decimalScale={2}
                          prefix="$"
                          className="w-24 px-2 py-1 text-xs text-right rounded border-gray-500 bg-gray-600 text-gray-200"
                          disabled={isVoid}
                        />
                        {!isVoid && (
                          <button
                            onClick={() => handleRemoveSurcharge(index)}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No surcharges</p>
                )}
                {surcharges.length > 0 && userRole !== "foreman" && (
                  <div className="mt-2 flex justify-end">
                    <span className="text-xs text-gray-400 mr-2">
                      Surcharges total:
                    </span>
                    <span className="text-sm font-medium text-orange-400">
                      ${formatCurrency(surchargesTotal)}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-2">
                {isPending && (
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => setShowVoidModal(true)}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Void
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                    {!isInventoryOrder && (
                      <>
                        <button
                          onClick={() => generatePDF(true)}
                          disabled={isSubmitting}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
                        >
                          {isSubmitting ? "Preparing..." : "Send Email"}
                        </button>
                        <button
                          onClick={() => generatePDF(false)}
                          disabled={isSubmitting}
                          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                          {isSubmitting ? "Generating..." : "Download PDF"}
                        </button>
                      </>
                    )}
                    <button
                      onClick={showDeliverConfirmation}
                      disabled={isSubmitting}
                      className="px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {isSubmitting ? "Delivering..." : "Mark Delivered"}
                    </button>
                  </div>
                )}
                {isDelivered && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {isSubmitting ? "Saving..." : "Save Surcharges"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ VARIANCE PANEL — vendor orders only ═══ */}
        {showVariancePanel &&
          varianceSummary.hasChanges &&
          isPending &&
          !isInventoryOrder && (
            <div className="w-[30%] min-w-[280px]">
              <div className="bg-gray-700 rounded-xl border border-orange-500/30 shadow-lg sticky top-6 overflow-hidden">
                <div className="px-4 py-3 bg-gray-800 border-b border-gray-600 flex justify-between items-center">
                  <h3 className="text-sm font-bold text-orange-400">
                    Variance Summary
                  </h3>
                  <button
                    onClick={() => setShowVariancePanel(false)}
                    className="text-gray-400 hover:text-white text-lg"
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {/* Material changes */}
                  {varianceSummary.changes.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                        Material Changes ({varianceSummary.changes.length})
                      </h4>
                      <div className="space-y-3">
                        {varianceSummary.changes.map((c, i) => (
                          <div
                            key={i}
                            className="p-3 rounded-lg text-xs border bg-gray-800 border-gray-600"
                          >
                            <div className="font-medium text-gray-200 mb-2">
                              {c.description}
                            </div>
                            {c.sizeChanged ? (
                              <div className="text-orange-400 text-[11px] mb-1">
                                Size: {c.originalSpec} → {c.newSpec}
                              </div>
                            ) : (
                              <div className="text-blue-400 text-[11px] mb-1">
                                Vendor swap: {c.originalSpec} → {c.newSpec}
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-2 text-[11px] mt-2">
                              <div>
                                <span className="text-gray-500">Original:</span>{" "}
                                <span className="text-gray-300">
                                  ${formatCurrency(c.origCost)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">New:</span>{" "}
                                <span className="text-gray-300">
                                  ${formatCurrency(c.newCost)}
                                </span>
                              </div>
                            </div>
                            <div
                              className={`text-right font-bold text-lg mt-2 ${c.delta >= 0 ? "text-red-400" : "text-green-400"}`}
                            >
                              {c.delta >= 0 ? "+" : ""}$
                              {formatCurrency(Math.abs(c.delta))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Labor changes */}
                  {varianceSummary.laborChanges.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">
                        Labor Adjustments ({varianceSummary.laborChanges.length}
                        )
                      </h4>
                      <p className="text-[10px] text-gray-500 mb-3">
                        Size changes affect linked labor coverage.
                      </p>
                      <div className="space-y-2">
                        {varianceSummary.laborChanges.map((lc, i) => {
                          const rk = lc.laborLineItemId,
                            dk = `${lc.materialLineItemId}_${lc.laborLineItemId}`;
                          const isRO = laborRateOverrides[rk] !== undefined,
                            isDO = laborDeltaOverrides[dk] !== undefined;
                          const dr = isRO
                            ? laborRateOverrides[rk]
                            : lc.unitRate;
                          const dd = isDO
                            ? laborDeltaOverrides[dk]
                            : lc.coverageDelta;
                          const dc = dd * dr;
                          return (
                            <div
                              key={i}
                              className="p-3 rounded-lg text-xs border border-gray-600 bg-gray-800"
                            >
                              <div className="text-gray-200 font-medium">
                                [{lc.laborId}] {lc.description}
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="text-gray-500 text-[10px]">
                                  Delta:
                                </span>
                                <NumericFormat
                                  value={dd}
                                  onValueChange={(v) => {
                                    const nd = v.floatValue ?? 0;
                                    setLaborDeltaOverrides((p) => {
                                      if (
                                        Math.abs(nd - lc.coverageDelta) < 0.01
                                      ) {
                                        const c = { ...p };
                                        delete c[dk];
                                        return c;
                                      }
                                      return { ...p, [dk]: nd };
                                    });
                                  }}
                                  decimalScale={1}
                                  allowNegative={true}
                                  className={`w-28 px-2 py-1 text-xs rounded border ${isDO ? "border-orange-400 bg-orange-900/30" : "border-gray-500 bg-gray-600"} text-gray-200`}
                                />
                                <span className="text-[10px] text-gray-500">
                                  [{lc.uom}]
                                </span>
                                {isDO && (
                                  <button
                                    onClick={() =>
                                      setLaborDeltaOverrides((p) => {
                                        const c = { ...p };
                                        delete c[dk];
                                        return c;
                                      })
                                    }
                                    className="text-orange-400 text-[9px]"
                                  >
                                    reset
                                  </button>
                                )}
                              </div>
                              <div className="mt-1 flex items-center gap-2">
                                <span className="text-gray-500 text-[10px]">
                                  Rate:
                                </span>
                                <NumericFormat
                                  value={dr}
                                  onValueChange={(v) => {
                                    const nr = v.floatValue ?? 0;
                                    setLaborRateOverrides((p) => {
                                      if (Math.abs(nr - lc.unitRate) < 0.001) {
                                        const c = { ...p };
                                        delete c[rk];
                                        return c;
                                      }
                                      return { ...p, [rk]: nr };
                                    });
                                  }}
                                  decimalScale={3}
                                  prefix="$"
                                  allowNegative={false}
                                  className={`w-24 px-2 py-1 text-xs rounded border ${isRO ? "border-orange-400 bg-orange-900/30" : "border-gray-500 bg-gray-600"} text-gray-200`}
                                />
                                {isRO && (
                                  <button
                                    onClick={() =>
                                      setLaborRateOverrides((p) => {
                                        const c = { ...p };
                                        delete c[rk];
                                        return c;
                                      })
                                    }
                                    className="text-orange-400 text-[9px]"
                                  >
                                    reset
                                  </button>
                                )}
                              </div>
                              <div
                                className={`text-right font-bold text-lg mt-2 ${dc >= 0 ? "text-red-400" : "text-green-400"}`}
                              >
                                {dc >= 0 ? "+" : ""}$
                                {formatCurrency(Math.abs(dc))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Variance reason */}
                  {/* Variance reason */}
                  <div className="pt-2 border-t border-gray-600">
                    <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">
                      Variance Reason <span className="text-red-400">*</span>
                    </label>
                    <div className="flex flex-col gap-2">
                      <select
                        value={varianceReasonCategory}
                        onChange={(e) => {
                          const val = e.target.value;
                          setVarianceReasonCategory(val);
                          if (val !== "Other" && val !== "") {
                            setVarianceReason("");
                          }
                        }}
                        className={`w-full rounded border bg-gray-600 text-gray-200 text-xs px-3 py-1.5 ${
                          !varianceReasonCategory
                            ? "border-red-500/50 focus:border-red-500"
                            : "border-gray-500 focus:border-orange-500"
                        }`}
                      >
                        <option value="">— Select reason category —</option>
                        <option value="Vendor Out of Stock">
                          Vendor Out of Stock
                        </option>
                        <option value="Better Pricing Available">
                          Better Pricing Available
                        </option>
                        <option value="Lead Time Too Long">
                          Lead Time Too Long
                        </option>
                        <option value="Site Condition Change">
                          Site Condition Change
                        </option>
                        <option value="Foreman Field Decision">
                          Foreman Field Decision
                        </option>
                        <option value="Estimation Error">
                          Estimation Error
                        </option>
                        <option value="Design / Engineering Change">
                          Design / Engineering Change
                        </option>
                        <option value="Vendor Cannot Fulfil Order">
                          Vendor Cannot Fulfil Order
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="pt-3 border-t border-gray-600">
                    <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Material:</span>
                        <span
                          className={`font-bold text-lg ${varianceSummary.totalMaterialDelta >= 0 ? "text-red-400" : "text-green-400"}`}
                        >
                          {varianceSummary.totalMaterialDelta >= 0 ? "+" : ""}$
                          {formatCurrency(
                            Math.abs(varianceSummary.totalMaterialDelta),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Labor:</span>
                        <span
                          className={`font-bold text-lg ${varianceSummary.totalLaborDelta >= 0 ? "text-red-400" : "text-green-400"}`}
                        >
                          {varianceSummary.totalLaborDelta >= 0 ? "+" : ""}$
                          {formatCurrency(
                            Math.abs(varianceSummary.totalLaborDelta),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-gray-700">
                        <span className="text-gray-200 font-semibold">
                          Total:
                        </span>
                        <span
                          className={`font-bold text-xl ${varianceSummary.totalDelta >= 0 ? "text-red-400" : "text-green-400"}`}
                        >
                          {varianceSummary.totalDelta >= 0 ? "+" : ""}$
                          {formatCurrency(Math.abs(varianceSummary.totalDelta))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Collapsed variance tab — vendor orders only */}
        {!showVariancePanel &&
          varianceSummary.hasChanges &&
          isPending &&
          !isInventoryOrder && (
            <button
              onClick={() => setShowVariancePanel(true)}
              className="fixed right-0 top-1/2 -translate-y-1/2 bg-orange-500 text-white px-1.5 py-3 rounded-l-lg shadow-lg hover:bg-orange-600 z-10 text-xs font-medium"
              style={{ writingMode: "vertical-rl" }}
            >
              Variance ({varianceSummary.changes.length})
            </button>
          )}
      </div>

      {/* ═══ VOID MODAL ═══ */}
      {showVoidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-700 rounded-lg border border-gray-600 shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-2">
              Void Stock-Out Order
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              This will restore pending quantities.
            </p>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Reason..."
              rows={3}
              className="w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 shadow-sm focus:border-orange-500 sm:text-sm mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowVoidModal(false)}
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isSubmitting ? "Voiding..." : "Void"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EMAIL MODAL ═══ */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-full max-w-lg border border-gray-600">
            <h2 className="text-xl font-semibold text-orange-400 mb-4">
              Send Stock-Out Order
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  From
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  To
                </label>
                <input
                  type="text"
                  value={emailForm.to}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, to: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 focus:border-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 focus:border-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Body
                </label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, body: e.target.value })
                  }
                  rows="5"
                  className="mt-1 block w-full rounded-md border-gray-500 bg-gray-600 text-gray-200 focus:border-orange-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowEmailForm(false);
                    setVendorPdfBuffer(null);
                    setSitePdfBuffer(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailSubmit}
                  disabled={!vendorPdfBuffer}
                  className="px-4 py-2 text-sm text-gray-200 bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
